"use client";

import React, { useState } from "react";
import Loader from "../common/Loader";
import IntegrationsCard from "./integrationsCard/IntegrationsCard";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

import ModalDropiIntegration from "./Modals/ModalDropiIntegration";
import ModalMercadoPagoIntegration from "./Modals/ModalMercadoPagoIntegration";

import { postDropiIntegration } from "../../services/integrations/integrationsService";
import {
  postRegisterSellerApi,
  registerSellerList,
  updatePaymentMethodActive,
  updatePaymentMethodInactive,
} from "../../services/payment/payment";

import { useIntegrations } from "./hooks/useIntegrations";
import { IntegrationType } from "./config/integrations.config";
import { handleApi } from "../../utils/handleApi";

const IntegrationsPanel: React.FC = () => {
  const { language, country } = useAuth();
  const { integrations, loading } = useIntegrations(country);

  const [modalOpenDropi, setModalOpenDropi] = useState(false);
  const [modalOpenMercadoPago, setModalOpenMercadoPago] = useState(false);

  const [tokenDropi, setTokenDropi] = useState("");
  const [tokenMercadoPagoPublic, setTokenMercadoPagoPublic] = useState("");
  const [tokenMercadoPagoPrivate, setTokenMercadoPagoPrivate] = useState("");
  const [loadingMercadoPago, setLoadingMercadoPago] = useState(false);

  const [loadingCard, setLoadingCard] = useState(false);

  const handleConnect = (type: IntegrationType) => {
    if (type === "dropi") setModalOpenDropi(true);
    if (type === "mercadopago") setModalOpenMercadoPago(true);
    if (type === "stripe") handlePostStripeIntegration(integrations.find((integration) => integration.type === "stripe")?.alreadyConfigured ?? false);
  };

  const handleCancel = async (type: IntegrationType) => {
    try {
      if (type === "dropi") {
        await postDropiIntegration({
          is_active: false,
          integration_token: "",
        });
      }

      if (type === "mercadopago") {
        await updatePaymentMethodInactive(registerSellerList.MERCADO_PAGO);
      }

      if (type === "stripe") {
        await updatePaymentMethodInactive(registerSellerList.STRIPE);
      }

      toast.success(dictionary["integration-canceled"][language]);
      window.location.reload();
    } catch (error: any) {
      toast.error(
        dictionary["integration-error"][language] +
          ": " +
          dictionary[error?.response?.data?.trigger]?.[language]
      );
    }
  };

  const handlePostDropiIntegration = async () => {
    try {
      await postDropiIntegration({
        integration_token: tokenDropi,
        is_active: true,
      });

      toast.success(dictionary["integration-added"][language]);
      setModalOpenDropi(false);
      setTokenDropi("");
      window.location.reload();
    } catch (error: any) {
      toast.error(dictionary["integration-error"][language]);
    }
  };

  const handlePostMercadoPagoIntegration = async () => {
    const id = registerSellerList.MERCADO_PAGO;
    setLoadingMercadoPago(true);

    try {
      await postRegisterSellerApi(id, {
        public_key: tokenMercadoPagoPublic,
        token: tokenMercadoPagoPrivate,
      });

      await updatePaymentMethodActive("mercadopago");

      toast.success(dictionary["integration-added"][language]);
      setModalOpenMercadoPago(false);
      setTokenMercadoPagoPublic("");
      setTokenMercadoPagoPrivate("");
      window.location.reload();
    } catch (error: any) {
      toast.error(dictionary["integration-error"][language]);
    } finally {
      setLoadingMercadoPago(false);
    }
  };

  const handlePostStripeIntegration = async (alreadyConfigured: boolean = false) => {
    setLoadingCard(true);
    if (alreadyConfigured) {
      const {res, error} = await handleApi(updatePaymentMethodActive(registerSellerList.STRIPE), false);
      if (error) {
        toast.error(dictionary["integration-error"][language]);
      }
      if (res) {
        toast.success(dictionary["integration-activated"][language]);
        window.location.reload();
      }
      setLoadingCard(false);
      return;
    }
    const { res, error } = await handleApi(
      postRegisterSellerApi(registerSellerList.STRIPE, {}),
      false
    );
    if (error) {
      toast.error(dictionary["integration-error"][language]);
    }
    if (res) {
      toast.success(dictionary["integration-added"][language]);
      if (res.config.onboarding_url) {
        window.open(res.config.onboarding_url, "_blank");
      }
    }
    setLoadingCard(false);
  };

  // TODO SKELETON?
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="bg-[#F5F6FA] px-4 md:p-8 md:ml-4">
      <h1 className="text-xl md:text-2xl font-bold mb-6">
        {dictionary["integrations"][language]}
      </h1>

      <div className="flex flex-wrap gap-6">
        {integrations.map((integration) => (
          <IntegrationsCard
            key={integration.type}
            name={integration.name}
            logoSrc={integration.logoSrc}
            bgColor={integration.bgColor}
            isActive={integration.isActive}
            isConnected={integration.isConnected}
            onConnect={() => handleConnect(integration.type)}
            onCancel={() => handleCancel(integration.type)}
            loading={loadingCard}
            type={integration.type}
          />
        ))}
      </div>

      {modalOpenDropi && (
        <ModalDropiIntegration
          setModalOpenDropi={setModalOpenDropi}
          handlePostDropiIntegration={handlePostDropiIntegration}
          token={tokenDropi}
          setToken={setTokenDropi}
          language={language}
        />
      )}

      {modalOpenMercadoPago && (
        <ModalMercadoPagoIntegration
          id="mercadopago"
          setModalOpenMercadoPago={setModalOpenMercadoPago}
          handlePostMercadoPagoIntegration={handlePostMercadoPagoIntegration}
          tokenPublic={tokenMercadoPagoPublic}
          setTokenPublic={setTokenMercadoPagoPublic}
          tokenPrivate={tokenMercadoPagoPrivate}
          setTokenPrivate={setTokenMercadoPagoPrivate}
          loading={loadingMercadoPago}
          language={language}
        />
      )}
    </div>
  );
};

export default IntegrationsPanel;
